import { supabase } from './supabaseClient';
import { Obra, ObraDocumento, ObraResponsavel, ObraResumo, ObraUnidadeInput, Unidade } from './types';

const mapObraRow = (row: any): Obra => {
    if (!row) return row;
    const { obra_investidores, unidades, ...rest } = row;
    const responsaveis: ObraResponsavel[] = Array.isArray(obra_investidores)
        ? obra_investidores
              .map((oi: any) => {
                  const inv = oi.investidores;
                  if (!inv) return null;
                  return {
                      id: inv.id,
                      nome: inv.nome,
                      cpf_cnpj: inv.cpf_cnpj,
                      contato: inv.contato,
                      percentual_participacao:
                          oi.percentual_participacao != null ? Number(oi.percentual_participacao) : null,
                  };
              })
              .filter(Boolean)
        : [];

    const unidadesList: Unidade[] = Array.isArray(unidades)
        ? [...unidades].sort((a, b) => String(a.identificacao || '').localeCompare(String(b.identificacao || ''), 'pt-BR'))
        : [];

    return {
        ...rest,
        responsaveis,
        unidades: unidadesList,
    };
};

// Obras
export const getObras = async (): Promise<Obra[]> => {
    const { data, error } = await supabase
        .from('obras')
        .select(`
            id,
            nome,
            endereco,
            municipio,
            estado,
            data_limite_correcao,
            cno,
            data_inicio,
            data_termino_prevista,
            status,
            orcamento_inicial,
            tipo_de_construcao,
            quantidade_unidades,
            valor_venda_unidade,
            inscricao_municipal,
            matricula_imovel,
            numero_alvara,
            obra_investidores (
                investidor_id,
                percentual_participacao,
                investidores (
                    id,
                    nome,
                    cpf_cnpj,
                    contato
                )
            ),
            unidades (
                id,
                obra_id,
                identificacao,
                descricao,
                valor,
                status,
                area,
                area_construida,
                area_nao_construida,
                area_privativa_total,
                quartos,
                banheiros,
                vagas,
                endereco,
                bairro,
                municipio,
                estado,
                cep,
                imagens
            )
        `)
        .order('nome');
    if (error) throw error;
    return (data || []).map(mapObraRow);
};

export const getObraById = async (id: number): Promise<Obra | null> => {
    const { data, error } = await supabase
        .from('obras')
        .select(`
            *,
            obra_investidores (
                investidor_id,
                percentual_participacao,
                investidores (
                    id,
                    nome,
                    cpf_cnpj,
                    contato
                )
            ),
            unidades (
                id,
                obra_id,
                identificacao,
                descricao,
                valor,
                status,
                area,
                area_construida,
                area_nao_construida,
                area_privativa_total,
                quartos,
                banheiros,
                vagas,
                endereco,
                bairro,
                municipio,
                estado,
                cep,
                imagens
            )
        `)
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    return data ? mapObraRow(data) : null;
};

export const addObra = async (obra: Omit<Obra, 'id'>) => {
    const { responsaveis: _r, unidades: _u, ...dbObra } = obra as any;
    const { data, error } = await supabase.from('obras').insert([dbObra]).select();
    if (error) throw error;
    return data;
};

export const updateObra = async (id: number, obra: Partial<Obra>) => {
    const { responsaveis: _r, unidades: _u, obra_investidores: _oi, ...dbObra } = obra as any;
    const { data, error } = await supabase.from('obras').update(dbObra).eq('id', id).select();
    if (error) throw error;
    return data;
};

export const syncObraResponsaveis = async (
    obraId: number,
    responsaveis: { investidor_id: number; percentual_participacao?: number | null }[]
) => {
    const { error: delError } = await supabase.from('obra_investidores').delete().eq('obra_id', obraId);
    if (delError) throw delError;

    if (responsaveis.length > 0) {
        const rows = responsaveis.map((r) => ({
            obra_id: obraId,
            investidor_id: r.investidor_id,
            percentual_participacao: r.percentual_participacao != null ? Number(r.percentual_participacao) : null,
        }));
        const { error: insError } = await supabase.from('obra_investidores').insert(rows);
        if (insError) throw insError;
    }
};

export const syncObraUnidades = async (
    obraId: number,
    obraLocation: { endereco?: string; municipio?: string; estado?: string },
    unidades: ObraUnidadeInput[]
) => {
    const { data: existingRows, error: fetchError } = await supabase
        .from('unidades')
        .select('id')
        .eq('obra_id', obraId);
    if (fetchError) throw fetchError;

    const existingIds = (existingRows || []).map((r: any) => Number(r.id));
    const incomingIds = unidades.filter((u) => u.id != null).map((u) => Number(u.id));
    const idsToDelete = existingIds.filter((id) => !incomingIds.includes(id));

    if (idsToDelete.length > 0) {
        const { error: delError } = await supabase.from('unidades').delete().in('id', idsToDelete);
        if (delError) throw delError;
    }

    for (const u of unidades) {
        const areaConstruida =
            u.area_construida != null && String(u.area_construida) !== '' ? Number(u.area_construida) : null;
        const areaNaoConstruida =
            u.area_nao_construida != null && String(u.area_nao_construida) !== ''
                ? Number(u.area_nao_construida)
                : null;
        const areaPrivativaExplicita =
            u.area_privativa_total != null && String(u.area_privativa_total) !== ''
                ? Number(u.area_privativa_total)
                : u.area != null && String(u.area) !== ''
                ? Number(u.area)
                : null;
        const areaPrivativaFinal =
            areaPrivativaExplicita ??
            (areaConstruida != null || areaNaoConstruida != null
                ? Number(((areaConstruida || 0) + (areaNaoConstruida || 0)).toFixed(2))
                : null);

        const payload = {
            obra_id: obraId,
            identificacao: u.identificacao.trim(),
            descricao: u.descricao?.trim() || null,
            valor: Number(u.valor) || 0,
            status: u.status || 'Disponível',
            area: areaPrivativaFinal,
            area_construida: areaConstruida,
            area_nao_construida: areaNaoConstruida,
            area_privativa_total: areaPrivativaFinal,
            quartos: u.quartos != null && String(u.quartos) !== '' ? Number(u.quartos) : null,
            banheiros: u.banheiros != null && String(u.banheiros) !== '' ? Number(u.banheiros) : null,
            vagas: u.vagas != null && String(u.vagas) !== '' ? Number(u.vagas) : null,
            endereco: obraLocation.endereco || null,
            municipio: obraLocation.municipio || null,
            estado: obraLocation.estado || null,
        };

        if (u.id) {
            const { error: updError } = await supabase
                .from('unidades')
                .update({ ...payload, updated_at: new Date().toISOString() })
                .eq('id', u.id);
            if (updError) throw updError;
        } else {
            const { error: insError } = await supabase
                .from('unidades')
                .insert([{ ...payload, imagens: [] }]);
            if (insError) throw insError;
        }
    }
};

export const deleteObra = async (obra_id: number) => {
    const { data, error } = await supabase.functions.invoke('delete-obra', {
        body: { obra_id },
    });
    if (error) throw error;
    return data;
};

// Edge Functions related to Obras (Migrated to RPCs)
export const getObrasResumo = async (): Promise<ObraResumo[]> => {
    const { data, error } = await supabase.rpc('get_obras_resumo');
    if (error) throw error;
    return (data || []).map((item: any) => ({
        ...item,
        custo_total: Number(item.custo_total) || 0,
        orcamento_inicial: Number(item.orcamento_inicial) || 0,
        quantidade_unidades: item.quantidade_unidades != null ? Number(item.quantidade_unidades) : null,
        valor_venda_unidade: item.valor_venda_unidade != null ? Number(item.valor_venda_unidade) : null,
        vgv_total: item.vgv_total != null ? Number(item.vgv_total) : null,
        responsaveis: Array.isArray(item.responsaveis) ? item.responsaveis : [],
    }));
};

export const getObraBalanco = async (obraId: number) => {
    const { data, error } = await supabase.rpc('get_obra_balanco', { p_obra_id: obraId });
    if (error) throw error;
    return data;
};

export const getObraCustosAgregados = async (obraId: number, groupBy: string, categoriaPaiId?: number) => {
    const { data, error } = await supabase.rpc('get_obra_custos_agregados', {
        p_obra_id: obraId,
        p_group_by: groupBy,
        in_categoria_pai_id: categoriaPaiId ?? null,
    });
    if (error) throw error;
    return data;
};

export const getObraValoresCorrigidos = async (obraId: number, dataLimite?: string) => {
    const { data, error } = await supabase.rpc('get_obra_valores_corrigidos', {
        p_obra_id: obraId,
        p_data_limite: dataLimite
    });

    if (error) {
        throw error;
    }

    return data;
};

// Obra Documentos
export const getObraDocumentos = async (obraId: number) => {
    const { data, error } = await supabase
        .from('obra_documentos')
        .select('*')
        .eq('obra_id', obraId)
        .order('criado_em', { ascending: false });

    if (error) throw error;
    return data as ObraDocumento[];
};

export const uploadObraDocumento = async (obraId: number, file: File, customName: string) => {
    // 1. Upload file to Storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${obraId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = fileName;

    const { error: uploadError } = await supabase.storage
        .from('obra-documentos')
        .upload(filePath, file);

    if (uploadError) throw uploadError;

    // 2. Insert record into database
    const { data, error: insertError } = await supabase
        .from('obra_documentos')
        .insert({
            obra_id: obraId,
            nome: customName || file.name, // Use custom name or fallback to file name
            caminho: filePath,
            tipo: file.type,
            tamanho: file.size,
            usuario_id: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

    if (insertError) {
        // If insert fails, try to delete the uploaded file to keep consistency
        await supabase.storage.from('obra-documentos').remove([filePath]);
        throw insertError;
    }

    return data as ObraDocumento;
};

export const getObraDocumentoUrl = async (caminho: string) => {
    const { data, error } = await supabase.storage
        .from('obra-documentos')
        .createSignedUrl(caminho, 3600); // Valid for 1 hour

    if (error) throw error;
    return data.signedUrl;
};

export const deleteObraDocumento = async (id: string, caminho: string) => {
    // 1. Delete from Storage
    const { error: storageError } = await supabase.storage
        .from('obra-documentos')
        .remove([caminho]);

    if (storageError) throw storageError;

    // 2. Delete from Database
    const { error: dbError } = await supabase
        .from('obra_documentos')
        .delete()
        .eq('id', id);

    if (dbError) throw dbError;
};

export const downloadObraDocumento = async (caminho: string) => {
    const { data, error } = await supabase.storage
        .from('obra-documentos')
        .download(caminho);

    if (error) throw error;
    return data;
};

// Gerenciamento de Usuários da Obra
export const getAllUsers = async () => {
    const { data, error } = await supabase.rpc('get_all_users');
    if (error) throw error;
    return data;
};

export const getObraUsers = async (obraId: number) => {
    const { data, error } = await supabase
        .from('obra_usuarios')
        .select('user_id, created_at')
        .eq('obra_id', obraId);
    if (error) throw error;
    return data;
};

export const assignUserToObra = async (obraId: number, userId: string) => {
    const { data, error } = await supabase
        .from('obra_usuarios')
        .insert({ obra_id: obraId, user_id: userId })
        .select()
        .single();
    if (error) throw error;
    return data;
};

export const removeUserFromObra = async (obraId: number, userId: string) => {
    const { error } = await supabase
        .from('obra_usuarios')
        .delete()
        .eq('obra_id', obraId)
        .eq('user_id', userId);
    if (error) throw error;
};
